<script lang="ts">
    import { getContext, onMount } from "svelte";
    import type { Writable } from "svelte/store";
    import {
        Routes,
        type AdminDifficultyParamsResponse,
        type AdminDifficultyPreviewResponse,
        type AdminPuzzleSummary,
        type AdminPuzzleRoutesResponse,
        type AdminRouteEdge,
        type AdminRouteNode,
    } from "@lib/types";
    import { difficultyGroupName, formatTime } from "@lib/util";
    import {
        adminDifficultyCacheBuild,
        adminDifficultyParams,
        adminDifficultyParamsClear,
        adminDifficultyParamsSet,
        adminDifficultyPreview,
        adminPuzzleRoutes,
        adminPuzzles,
    } from "@lib/api";
    import BackButton from "@components/common/BackButton.svelte";
    import ResetTimer from "@components/ResetTimer.svelte";
    import GridPreview from "@components/puzzle/GridPreview.svelte";

    let router = getContext<Writable<Routes>>("router");

    let items: AdminPuzzleSummary[] = $state([]);
    let loading = $state(false);
    let loadError: string | null = $state(null);

    let limit = $state(50);
    let offset = $state(0);
    let total = $state(0);
    let expandedId: number | null = $state(null);

    let routesLoadingId: number | null = $state(null);
    let routesError: string | null = $state(null);
    let routesByPuzzleId: Record<number, AdminPuzzleRoutesResponse> = $state({});

    type Selection =
        | { kind: "node"; puzzleId: number; sig: string }
        | { kind: "edge"; puzzleId: number; from: string; to: string };
    let selection: Selection | null = $state(null);

    let tuningLoading = $state(false);
    let tuningError: string | null = $state(null);
    let tuningParams: AdminDifficultyParamsResponse | null = $state(null);
    let tuningJson = $state("{}\n");
    let previewById: Record<number, number> = $state({});
    let previewMissing: number[] = $state([]);
    let previewLoading = $state(false);

    const visiblePuzzleIds = (): number[] => items.map((p) => p.id);

    const loadTuningParams = async () => {
        tuningLoading = true;
        tuningError = null;
        try {
            const resp = await adminDifficultyParams();
            tuningParams = resp;
            const seed = resp.saved && Object.keys(resp.saved).length ? resp.saved : {};
            tuningJson = JSON.stringify(seed, null, 2) + "\n";
        } catch (e) {
            tuningError = e instanceof Error && e.message ? e.message : "Failed to load tuning params";
        } finally {
            tuningLoading = false;
        }
    };

    const parseOverride = (): Record<string, unknown> | null => {
        try {
            const parsed = JSON.parse(tuningJson);
            if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return null;
            return parsed as Record<string, unknown>;
        } catch {
            return null;
        }
    };

    const buildCacheForPage = async () => {
        tuningLoading = true;
        tuningError = null;
        try {
            await adminDifficultyCacheBuild(visiblePuzzleIds());
        } catch (e) {
            tuningError = e instanceof Error && e.message ? e.message : "Failed to build cache";
        } finally {
            tuningLoading = false;
        }
    };

    const runPreview = async () => {
        previewLoading = true;
        tuningError = null;
        try {
            const override = parseOverride();
            if (override === null) {
                tuningError = "Invalid JSON";
                return;
            }
            const resp: AdminDifficultyPreviewResponse = await adminDifficultyPreview(visiblePuzzleIds(), override);
            const next: Record<number, number> = {};
            for (const it of resp.items) {
                next[it.puzzle_id] = it.difficulty;
            }
            previewById = next;
            previewMissing = resp.missing;
        } catch (e) {
            tuningError = e instanceof Error && e.message ? e.message : "Failed to preview";
        } finally {
            previewLoading = false;
        }
    };

    const saveOverride = async () => {
        tuningLoading = true;
        tuningError = null;
        try {
            const override = parseOverride();
            if (override === null) {
                tuningError = "Invalid JSON";
                return;
            }
            await adminDifficultyParamsSet(override);
            await loadTuningParams();
        } catch (e) {
            tuningError = e instanceof Error && e.message ? e.message : "Failed to save";
        } finally {
            tuningLoading = false;
        }
    };

    const clearOverride = async () => {
        tuningLoading = true;
        tuningError = null;
        try {
            await adminDifficultyParamsClear();
            await loadTuningParams();
            previewById = {};
            previewMissing = [];
        } catch (e) {
            tuningError = e instanceof Error && e.message ? e.message : "Failed to clear";
        } finally {
            tuningLoading = false;
        }
    };

    const load = async () => {
        loading = true;
        loadError = null;
        try {
            const resp = await adminPuzzles(limit, offset);
            items = resp.items;
            total = resp.total;
        } catch (e) {
            loadError = e instanceof Error && e.message ? e.message : "Failed to load puzzles";
        } finally {
            loading = false;
        }
    };

    onMount(() => {
        void load();
        void loadTuningParams();
    });

    const goToMenu = () => {
        router.set(Routes.Menu);
        window.history.back();
    };

    const toggleExpanded = (id: number) => {
        expandedId = expandedId === id ? null : id;
        selection = null;

        if (expandedId !== null && !routesByPuzzleId[expandedId]) {
            void loadRoutes(expandedId);
        }
    };

    const loadRoutes = async (puzzleId: number) => {
        routesLoadingId = puzzleId;
        routesError = null;
        try {
            const resp = await adminPuzzleRoutes(puzzleId, 500, false);
            routesByPuzzleId = { ...routesByPuzzleId, [puzzleId]: resp };
        } catch (e) {
            routesError = e instanceof Error && e.message ? e.message : "Failed to load routes";
        } finally {
            routesLoadingId = null;
        }
    };

    const topOutgoingEdges = (routes: AdminPuzzleRoutesResponse, fromSig: string): AdminRouteEdge[] => {
        return routes.edges
            .filter((e) => e.from === fromSig)
            .sort((a, b) => b.count - a.count)
            .slice(0, 6);
    };

    const stuckNodes = (routes: AdminPuzzleRoutesResponse): AdminRouteNode[] => {
        return routes.nodes
            .slice()
            .sort((a, b) => b.total_dt - a.total_dt)
            .slice(0, 10);
    };

    const avgDt = (n: AdminRouteNode): number => {
        const v = Math.max(1, n.visits || 0);
        return Math.round((n.total_dt || 0) / v);
    };

    const markerIndicesFromRates = (rates: number[] | null | undefined) => {
        if (!rates || !rates.length) return [] as number[];
        const out: number[] = [];
        for (let i = 0; i < rates.length; i++) {
            const v = rates[i];
            if (typeof v === "number" && Number.isFinite(v) && v > 0) {
                out.push(i);
            }
        }
        return out;
    };

    const shortSig = (sig: string) => {
        if (!sig) return "start";
        const parts = sig.split(",");
        if (parts.length <= 4) return sig;
        return `${parts.slice(0, 4).join(",")},… (+${parts.length - 4})`;
    };

    const selectedNode = (routes: AdminPuzzleRoutesResponse): AdminRouteNode | null => {
        const sel = selection;
        if (!sel || sel.puzzleId !== routes.puzzle_id) return null;
        if (sel.kind === "node") {
            return routes.nodes.find((n) => n.sig === sel.sig) ?? null;
        }
        return routes.nodes.find((n) => n.sig === sel.to) ?? null;
    };

    const selectedEdge = (routes: AdminPuzzleRoutesResponse): AdminRouteEdge | null => {
        const sel = selection;
        if (!sel || sel.puzzleId !== routes.puzzle_id) return null;
        if (sel.kind !== "edge") return null;
        return routes.edges.find((e) => e.from === sel.from && e.to === sel.to) ?? null;
    };

    const selectedStuckSig = (routes: AdminPuzzleRoutesResponse): string | null => {
        const sel = selection;
        if (!sel || sel.puzzleId !== routes.puzzle_id) return null;
        return sel.kind === "node" ? sel.sig : sel.from;
    };

    const isSelectedNode = (puzzleId: number, sig: string) => {
        return selection?.kind === "node" && selection.puzzleId === puzzleId && selection.sig === sig;
    };

    const isSelectedEdge = (puzzleId: number, from: string, to: string) => {
        return selection?.kind === "edge" && selection.puzzleId === puzzleId && selection.from === from && selection.to === to;
    };

    const toggleSelectNode = (puzzleId: number, sig: string) => {
        if (isSelectedNode(puzzleId, sig)) {
            selection = null;
        } else {
            selection = { kind: "node", puzzleId, sig };
        }
    };

    const toggleSelectEdge = (puzzleId: number, from: string, to: string) => {
        if (isSelectedEdge(puzzleId, from, to)) {
            selection = null;
        } else {
            selection = { kind: "edge", puzzleId, from, to };
        }
    };

    const weekdayForIso = (iso: string): string => {
        const d = new Date(`${iso}T00:00:00`);
        return d.toLocaleDateString("en-us", { weekday: "long" });
    };

    const solveRate = (p: AdminPuzzleSummary) => {
        if (!p.attempts_counter) return 0;
        return p.solved_counter / p.attempts_counter;
    };

    const nextPage = async () => {
        if (offset + limit >= total) return;
        offset = offset + limit;
        expandedId = null;
        await load();
    };

    const prevPage = async () => {
        if (offset <= 0) return;
        offset = Math.max(0, offset - limit);
        expandedId = null;
        await load();
    };
</script>

<ResetTimer />

<div class="container">
    <main>
        <header class="header">
            <div>
                <BackButton onclick={goToMenu} />
            </div>
            <div class="title">
                <h3>TWO KINGS</h3>
                <h5>Admin</h5>
            </div>
            <div class="right"></div>
        </header>

        <section>
            <h1>All puzzles</h1>

            {#if loadError}
                <div class="error">{loadError}</div>
            {/if}

            <div class="toolbar">
                <button class="button" type="button" onclick={load} disabled={loading}>
                    {#if loading}
                        Loading...
                    {:else}
                        Refresh
                    {/if}
                </button>

                <div class="pager">
                    <button class="button" type="button" onclick={prevPage} disabled={loading || offset <= 0}>
                        Prev
                    </button>
                    <button
                        class="button"
                        type="button"
                        onclick={nextPage}
                        disabled={loading || offset + limit >= total}
                    >
                        Next
                    </button>
                    <div class="pager-meta monospace">
                        {#if total > 0}
                            {offset + 1}-{Math.min(offset + limit, total)} of {total}
                        {:else}
                            0
                        {/if}
                    </div>
                </div>
            </div>

            <div class="panel tuning">
                <div class="tuning-head">
                    <h3>Difficulty tuning</h3>
                    <div class="tuning-actions">
                        <button class="button" type="button" onclick={loadTuningParams} disabled={tuningLoading}>
                            Reload params
                        </button>
                        <button class="button" type="button" onclick={buildCacheForPage} disabled={tuningLoading || loading || !items.length}>
                            Build cache for page
                        </button>
                        <button class="button" type="button" onclick={runPreview} disabled={previewLoading || loading || !items.length}>
                            {#if previewLoading}
                                Previewing...
                            {:else}
                                Preview
                            {/if}
                        </button>
                        <button class="button" type="button" onclick={saveOverride} disabled={tuningLoading}>
                            Save override
                        </button>
                        <button class="button" type="button" onclick={clearOverride} disabled={tuningLoading}>
                            Clear override
                        </button>
                    </div>
                </div>

                {#if tuningError}
                    <div class="error">{tuningError}</div>
                {/if}

                {#if previewMissing.length}
                    <div class="mono dim">
                        Missing cache for {previewMissing.length} puzzles. Click “Build cache for page” then preview again.
                    </div>
                {/if}

                <div class="tuning-editor">
                    <textarea class="mono" bind:value={tuningJson} rows={10}></textarea>
                    <div class="mono dim">
                        JSON override applied on top of active params. Leave empty object to preview current.
                    </div>
                </div>
            </div>

            <div class="table-wrap">
                <table>
                    <thead>
                        <tr>
                            <th>ID</th>
                            <th>Date</th>
                            <th>Difficulty</th>
                            <th>Attempts</th>
                            <th>Solves</th>
                            <th>Solve %</th>
                            <th>Avg time</th>
                            <th>Avg hints</th>
                        </tr>
                    </thead>
                    <tbody>
                        {#each items as p (p.id)}
                            <tr class="row" class:expanded={expandedId === p.id} onclick={() => toggleExpanded(p.id)}>
                                <td class="mono">{p.id}</td>
                                <td class="mono">{p.date} ({weekdayForIso(p.date)})</td>
                                <td class="mono">
                                    {p.difficulty} ({difficultyGroupName(p.difficulty)})
                                    {#if previewById[p.id] !== undefined}
                                        {@const newDiff = previewById[p.id]}
                                        {@const delta = newDiff - p.difficulty}
                                        <span class="preview-delta">
                                            → {newDiff} ({delta >= 0 ? "+" : ""}{delta})
                                        </span>
                                    {/if}
                                </td>
                                <td class="mono">{p.attempts_counter}</td>
                                <td class="mono">{p.solved_counter}</td>
                                <td class="mono">{(solveRate(p) * 100).toFixed(0)}%</td>
                                <td class="mono">{p.avg_time ? formatTime(p.avg_time) : "-"}</td>
                                <td class="mono">{p.avg_hints ? p.avg_hints.toFixed(2) : "-"}</td>
                            </tr>

                            {#if expandedId === p.id}
                                <tr class="expanded-row">
                                    <td colspan={8}>
                                        <div class="drilldown">
                                            <div class="drilldown-left">
                                                {#if routesByPuzzleId[p.id]}
                                                    {@const r = routesByPuzzleId[p.id]}
                                                    {@const n = selectedNode(r)}
                                                    {@const e = selectedEdge(r)}
                                                    {@const stuckSig = selectedStuckSig(r)}
                                                    {@const kings = n ? n.kings : null}
                                                    {@const kingOrderIndices = n?.king_order ?? null}
                                                    {@const top = e ? e.x_top : n ? n.x_top : []}
                                                    {@const markerRates = e?.x_rate ?? n?.x_rate ?? null}
                                                    {@const markers = markerRates ? markerIndicesFromRates(markerRates) : top.map((m) => m.index)}

                                                    <div class="preview-wrap">
                                                        <GridPreview puzzle={p} {kings} {kingOrderIndices} {markerRates} {markers} />

                                                        <div class="preview-meta mono">
                                                            {#if selection}
                                                                <div>
                                                                    {#if selection.kind === "node"}
                                                                        stuck state {shortSig(selection.sig)}
                                                                    {:else}
                                                                        after move {shortSig(selection.from)} → {shortSig(selection.to)}
                                                                    {/if}
                                                                </div>
                                                            {/if}
                                                        </div>

                                                        {#if stuckSig !== null}
                                                            {@const outgoing = topOutgoingEdges(r, stuckSig)}
                                                            <div class="next-moves">
                                                                <div class="next-moves-head mono">
                                                                    Next moves from {shortSig(stuckSig)}
                                                                </div>
                                                                <div class="next-moves-list">
                                                                    {#each outgoing as ee (`${ee.from}->${ee.to}`)}
                                                                        <button
                                                                            class="edge mono selectable"
                                                                            class:selected={isSelectedEdge(p.id, ee.from, ee.to)}
                                                                            type="button"
                                                                            onclick={() => toggleSelectEdge(p.id, ee.from, ee.to)}
                                                                        >
                                                                            <div class="edge-to">→ {shortSig(ee.to)}</div>
                                                                            <div class="edge-meta">
                                                                                {(ee.share * 100).toFixed(0)}% ({ee.count}) · p95 {formatTime(ee.p95_dt)}
                                                                            </div>
                                                                        </button>
                                                                    {/each}

                                                                    {#if outgoing.length === 0}
                                                                        <div class="dim mono">No recorded next moves from this state</div>
                                                                    {/if}
                                                                </div>
                                                            </div>
                                                        {/if}
                                                    </div>
                                                {:else}
                                                    <div class="preview-wrap">
                                                        <GridPreview puzzle={p} />
                                                        <div class="preview-meta mono">
                                                            <div class="dim">Click refresh to load routes</div>
                                                        </div>
                                                    </div>
                                                {/if}
                                            </div>

                                            <div class="drilldown-right">
                                                <div class="routes">
                                                    <div class="routes-head">
                                                        <h3>Routes</h3>
                                                        <button
                                                            class="button"
                                                            type="button"
                                                            onclick={() => loadRoutes(p.id)}
                                                            disabled={routesLoadingId === p.id}
                                                        >
                                                            {#if routesLoadingId === p.id}
                                                                Loading...
                                                            {:else}
                                                                Refresh
                                                            {/if}
                                                        </button>
                                                    </div>

                                                    {#if routesError}
                                                        <div class="error">{routesError}</div>
                                                    {/if}

                                                    {#if routesByPuzzleId[p.id]}
                                                        {@const r = routesByPuzzleId[p.id]}
                                                        <div class="routes-meta mono">
                                                            attempts used: {r.attempts_used} · nodes: {r.nodes.length} · edges: {r.edges.length}
                                                        </div>

                                                        <div class="panel">
                                                            <h4>Stuck states</h4>
                                                            <div class="hint mono">Avg = total / visits. Sorted by total.</div>
                                                            <table class="mini">
                                                                <thead>
                                                                    <tr>
                                                                        <th>State</th>
                                                                        <th>Visits</th>
                                                                        <th>Avg</th>
                                                                        <th>Total</th>
                                                                        <th>p95</th>
                                                                        <th>Hints</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {#each stuckNodes(r) as nn (nn.sig)}
                                                                        <tr
                                                                            class="selectable"
                                                                            class:selected={isSelectedNode(p.id, nn.sig)}
                                                                            onclick={() => toggleSelectNode(p.id, nn.sig)}
                                                                        >
                                                                            <td class="mono">{shortSig(nn.sig)}</td>
                                                                            <td class="mono">{nn.visits}</td>
                                                                            <td class="mono">{formatTime(avgDt(nn))}</td>
                                                                            <td class="mono">{formatTime(nn.total_dt)}</td>
                                                                            <td class="mono">{formatTime(nn.p95_dt)}</td>
                                                                            <td class="mono">{nn.hints}</td>
                                                                        </tr>
                                                                    {/each}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    {:else if routesLoadingId === p.id}
                                                        <div class="mono">Loading routes...</div>
                                                    {:else}
                                                        <div class="mono">Click refresh to load routes</div>
                                                    {/if}
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                            {/if}
                        {/each}
                    </tbody>
                </table>
            </div>
        </section>
    </main>
</div>

<style>
    .container {
        padding: 0 1rem;
        background-color: var(--bg);
        color: var(--text);
        animation: 0.25s fade ease;
    }

    main {
        max-width: 72rem;
        margin: 0 auto;
        min-height: 100vh;
        padding-bottom: 3rem;
    }

    .header {
        padding: 1rem 0;
        display: grid;
        grid-template-columns: 64px 1fr 64px;
        align-items: center;
    }

    .title {
        text-align: center;
    }

    section {
        padding: 1rem 0;
        display: flex;
        flex-direction: column;
        gap: 1rem;
    }

    h1 {
        font-size: 2.25rem;
    }

    .toolbar {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 0.5rem;
    }

    .pager {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        margin-left: auto;
    }

    .pager-meta {
        opacity: 0.8;
    }

    .error {
        border: 2px solid var(--primary);
        border-radius: 8px;
        padding: 0.75rem;
        background-color: var(--cell-red);
        color: var(--black);
    }

    .table-wrap {
        overflow-x: auto;
        border: 1px solid var(--bg-opacity);
        border-radius: 8px;
    }

    table {
        border-collapse: collapse;
        width: 100%;
        font-size: 0.9rem;
    }

    thead {
        background-color: var(--bg-opacity);
    }

    th,
    td {
        padding: 0.6rem 0.75rem;
        text-align: left;
        border-bottom: 1px solid var(--bg-opacity);
        white-space: nowrap;
    }

    .mono {
        font-family: "Roboto Condensed", monospace;
        font-weight: 700;
        font-style: normal;
        font-optical-sizing: auto;
    }

    tr.row {
        cursor: pointer;
    }

    tr.row:hover td {
        background-color: var(--bg-opacity);
    }

    tr.row.expanded td {
        background-color: var(--bg-opacity);
    }

    tr.expanded-row td {
        padding: 0.75rem;
    }

    .drilldown {
        display: grid;
        grid-template-columns: minmax(320px, 420px) 1fr;
        gap: 0.75rem;
        align-items: start;
    }

    @media (max-width: 1000px) {
        .drilldown {
            grid-template-columns: 1fr;
        }
    }

    .drilldown-left {
        border: 1px solid var(--bg-opacity);
        border-radius: 8px;
        padding: 0.75rem;
        background-color: color-mix(in srgb, var(--bg) 92%, var(--bg-opacity));
    }

    @media (min-width: 1001px) {
        .drilldown-left {
            position: sticky;
            top: 0.75rem;
        }
    }

    .drilldown-right {
        min-width: 0;
    }

    .preview-wrap {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
    }

    .preview-meta {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 0.75rem;
    }

    .selectable {
        cursor: pointer;
    }

    tr.selectable:hover td {
        background-color: var(--bg-opacity);
    }

    tr.selected td {
        outline: 2px solid var(--accent-color);
        outline-offset: -2px;
    }

    .routes {
        border: 1px solid var(--bg-opacity);
        border-radius: 8px;
        overflow: hidden;
    }

    .routes-head {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 0.75rem;
        padding: 0.75rem;
        background-color: var(--bg-opacity);
    }

    .routes-head h3 {
        margin: 0;
    }

    .routes-meta {
        padding: 0.75rem;
        opacity: 0.9;
        border-top: 1px solid var(--bg-opacity);
    }

    .routes .panel {
        margin: 0.75rem;
    }

    .panel {
        border: 1px solid var(--bg-opacity);
        border-radius: 8px;
        padding: 0.75rem;
        background-color: color-mix(in srgb, var(--bg) 92%, var(--bg-opacity));
    }

    .tuning-head {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 0.75rem;
        flex-wrap: wrap;
    }

    .tuning-actions {
        display: flex;
        gap: 0.5rem;
        flex-wrap: wrap;
        align-items: center;
    }

    .tuning-editor textarea {
        width: 100%;
        resize: vertical;
        border: 1px solid var(--bg-opacity);
        border-radius: 8px;
        padding: 0.75rem;
        background-color: var(--bg);
        color: var(--text);
        box-sizing: border-box;
    }

    .preview-delta {
        opacity: 0.85;
        margin-left: 0.5rem;
    }

    .panel h4 {
        margin: 0 0 0.5rem 0;
    }

    .hint {
        opacity: 0.8;
        margin-bottom: 0.5rem;
    }

    table.mini {
        width: 100%;
        border-collapse: collapse;
        font-size: 0.9rem;
    }

    table.mini th,
    table.mini td {
        padding: 0.5rem 0.6rem;
        border-bottom: 1px solid var(--bg-opacity);
    }

    table.mini thead {
        background-color: transparent;
    }

    .dim {
        opacity: 0.8;
    }

    .next-moves {
        border-top: 1px solid var(--bg-opacity);
        padding-top: 0.5rem;
    }

    .next-moves-head {
        opacity: 0.85;
        margin-bottom: 0.5rem;
    }

    .next-moves-list {
        display: flex;
        flex-direction: column;
        border: 1px solid var(--bg-opacity);
        border-radius: 8px;
        overflow: hidden;
        background-color: var(--bg);
    }

    .edge {
        padding: 0.55rem 0.75rem;
        border-top: 1px solid var(--bg-opacity);
        display: flex;
        align-items: baseline;
        justify-content: space-between;
        gap: 0.75rem;
        flex-wrap: wrap;
        width: 100%;
        border-left: 0;
        border-right: 0;
        border-bottom: 0;
        background-color: transparent;
        text-align: left;
        color: var(--text);
        font: inherit;
    }

    .edge:hover {
        background-color: var(--bg-opacity);
    }

    .edge.selected {
        outline: 2px solid var(--accent-color);
        outline-offset: -2px;
    }

    .edge-to {
        font-weight: 700;
    }

    .edge-meta {
        opacity: 0.85;
    }
</style>
